import React, { useState, useEffect } from "react"
import "./DashboardPage.css"
import axios from "../../api/axios_client";

const DashboardPage = () => {
  const [users, setUsers] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const response = await axios.get("/api/admin/users")
      if (response.status !== 200) {
        throw new Error("Failed to fetch users")
      }
      const data = await response.data
      setUsers(data)
      setError(null)
    } catch (err) {
      setError("Failed to fetch users. Please try again later.")
    } finally {
      setLoading(false)
    }
  }

  const totalUsers = users.length
  const activeUsers = users.filter((user) => user.access !== "1").length
  const countries = [...new Set(users.map((user) => user.country).filter(Boolean))]
  const totalCountries = countries.length

  const usersByCountry = countries
    .map((country) => ({
      country,
      count: users.filter((user) => user.country === country).length,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)

  const usersByRole = [
    { role: "Super Admin", count: users.filter((user) => user.access === "3").length },
    { role: "Admin", count: users.filter((user) => user.access === "2").length },
    { role: "User", count: users.filter((user) => user.access === "1").length },
  ]

  const recentUsers = users
    .sort((a, b) => b.id - a.id)
    .slice(0, 5)
    .map((user) => ({
      id: user.id,
      name: user.username,
      email: user.email,
      role: user.access === "1" ? "User" : user.access === "2" ? "Admin" : "Super Admin",
      country: user.country ?? "N/A",
    }))

  const metrics = [
    {
      title: "Total Users",
      value: totalUsers,
      change: "+12.5%",
      trend: "up",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="metric-icon"
        >
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
      ),
    },
    {
      title: "Active Users",
      value: activeUsers,
      change: "+8.2%",
      trend: "up",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="metric-icon"
        >
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
      ),
    },
    {
      title: "Countries",
      value: totalCountries,
      change: "+4.6%",
      trend: "up",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="metric-icon"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="2" y1="12" x2="22" y2="12"></line>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
        </svg>
      ),
    },
    {
      title: "New Users",
      value: recentUsers.length,
      change: "+2.5%",
      trend: "up",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="metric-icon"
        >
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <line x1="19" y1="8" x2="19" y2="14"></line>
          <line x1="22" y1="11" x2="16" y2="11"></line>
        </svg>
      ),
    },
  ]

  if (loading) {
    return <div className="loading">Loading...</div>
  }

  if (error) {
    return <div className="error">{error}</div>
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div>
          <h1>Dashboard</h1>
          <p>Manage your platform statistics and user analytics</p>
        </div>
      </div>

      <div className="metrics-grid">
        {metrics.map((metric, index) => (
          <div key={index} className="metric-card">
            <div className="metric-icon-wrapper">{metric.icon}</div>
            <div className="metric-content">
              <h3>{metric.title}</h3>
              <div className="metric-value">
                <span className="number">{metric.value}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="dashboard-grid">
        <div className="recent-activity">
          <div className="section-header">
            <h2>Recent Users</h2>
            {/* <div className="search-bar">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div> */}
          </div>
          <table className="users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Country</th>
                <th>Role</th>
              </tr>
            </thead>
            <tbody>
              {recentUsers.map((user) => (
                <tr key={user.id}>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>{user.country ?? "N/A"}</td>
                  <td>
                    <span className={`status-badge ${user.role.toLowerCase()}`}>{user.role}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="analytics-grid">
          <div className="analytics-card">
            <h3>Users by Country</h3>
            <div className="analytics-list">
              {usersByCountry.map((item, index) => (
                <div key={index} className="analytics-item">
                  <span>{item.country || "Unknown"}</span>
                  <span className="count">{item.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="analytics-card">
            <h3>Users by Role</h3>
            <div className="analytics-list">
              {usersByRole.map((item, index) => (
                <div key={index} className="analytics-item">
                  <span>{item.role}</span>
                  <span className="count">{item.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardPage
